// src/pages/EnrollStudent.jsx
// Admin page to register a new student and capture their face descriptors

import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFaceRecognition } from "../hooks/useFaceRecognition";
import { saveStudentProfile, saveFaceDescriptors, uploadFaceSample, getStudent, getNextRollNumber, incrementRollNumber } from "../firebase/database";
import { todayStr, getInitials } from "../utils/helpers";
import { theme } from "../utils/theme";

const C = theme.colors;

const BATCHES = ["Batch A", "Batch B", "Batch C", "Morning Batch", "Evening Batch"];

export default function EnrollStudent({ isUpdate = false }) {
  const { id } = useParams();
  const videoRef  = useRef();
  const streamRef = useRef();
  const navigate = useNavigate();

  const [step, setStep]     = useState(isUpdate ? 2 : 1);
  const [form, setForm]     = useState({ name: "", roll: "", batch: "Batch A", email: "", phone: "", familyPhone: "", role: "student" });
  const [capturing, setCapturing] = useState(false);
  const [samples, setSamples]     = useState(0);
  const [capturedDesc, setCapturedDesc] = useState([]);
  const [enrolledId, setEnrolledId]   = useState(isUpdate ? id : "");
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");

  const { modelsLoaded, loading: modelsLoading, error: modelError, captureDescriptors } = useFaceRecognition();

  useEffect(() => {
    if (isUpdate && id) {
      console.log(`[Enroll] Loading existing data for: ${id}`);
      getStudent(id).then(data => {
        if (data) {
          setForm({ 
            name: data.name || "", 
            roll: data.roll || "", 
            batch: data.batch || "Batch A", 
            email: data.email || "", 
            phone: data.phone || "", 
            familyPhone: data.familyPhone || "",
            role: data.role || (id.startsWith("STAFF") ? "staff" : "student")
          });
          setEnrolledId(id);
        } else {
          setError(`User with ID ${id} not found.`);
        }
      });
    } else {
      getNextRollNumber().then(n => setForm(f => ({ ...f, roll: String(n) })));
    }
  }, [id, isUpdate]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640 } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
    } catch (e) { setError("Camera error: " + e.message); }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const [capturedImage, setCapturedImage] = useState(null);

  const startCapture = async () => {
    setError("");
    setCapturing(true);
    setSamples(0);
    setCapturedImage(null);
    await startCamera();
    await new Promise(r => setTimeout(r, 1000)); // let camera warm up

    try {
      const descriptors = await captureDescriptors(
        videoRef.current,
        5,
        (done) => {
          setSamples(done);
          // Capture a representative frame on the 3rd sample
          if (done === 3 && videoRef.current) {
            const canvas = document.createElement("canvas");
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext("2d");
            // Flip horizontal to match video preview
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(videoRef.current, 0, 0);
            setCapturedImage(canvas.toDataURL("image/jpeg", 0.7));
          }
        }
      );
      setCapturedDesc(descriptors);
      stopCamera();
      setCapturing(false);
      setStep(3);
    } catch (e) {
      setError(e.message);
      setCapturing(false);
      stopCamera();
    }
  };

  const saveAndFinish = async () => {
    if (capturedDesc.length === 0) {
      setError("No face samples captured. Please go back and capture your face.");
      return;
    }
    console.log("[Enroll] Starting save process for student/staff...");
    setSaving(true);
    try {
      const studentId = isUpdate ? id : (form.role === "staff" ? `STAFF-${form.roll}` : `S${String(form.roll).padStart(3, "0")}`);
      setEnrolledId(studentId);
      console.log(`[Enroll] Generated ID: ${studentId}`);
      
      let photoURL = null;
      if (capturedImage) {
        console.log("[Enroll] Step 1: Uploading captured image...");
        try {
          photoURL = await uploadFaceSample(studentId, "profile", capturedImage);
        } catch (uploadErr) {
          console.error("[Enroll] Photo upload failed (likely CORS). Proceeding without photo.", uploadErr);
          // We continue because the face descriptors are more important for attendance
        }
      }

      console.log("[Enroll] Step 2: Saving profile to Firestore...");
      await saveStudentProfile(studentId, { 
        id: studentId, 
        ...form, 
        photoURL,
        faceEnrolled: true 
      });

      console.log("[Enroll] Step 3: Saving face descriptors to RTDB...");
      await saveFaceDescriptors(studentId, capturedDesc);
      
      if (form.role === "student") {
        console.log("[Enroll] Step 4: Incrementing student roll number...");
        await incrementRollNumber();
      }
      
      console.log("[Enroll] Save completed successfully!");
      setStep(4);
    } catch (e) {
      console.error("[Enroll] Save failed:", e);
      setError("Save failed: " + e.message);
    }
    setSaving(false);
  };

  const reset = () => { 
    setStep(1); 
    setSamples(0); 
    setCapturedDesc([]); 
    setEnrolledId("");
    setError(""); 
    getNextRollNumber().then(n => setForm({ name:"", roll:String(n), batch:"Batch A", email:"", phone:"", familyPhone: "", role: "student" }));
  };

  const inp = (key, placeholder, label, type = "text") => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>{label}</label>
      <input type={type} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
        style={{ width: "100%", background: "#F9FAFB", border: `1px solid ${C.border}`, borderRadius: 9, padding: "10px 13px", fontSize: 14, color: C.text, outline: "none", fontFamily: "DM Sans" }} />
    </div>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap'); *{box-sizing:border-box;}`}</style>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 6 }}>Enroll New Student</div>
      <div style={{ fontSize: 14, color: C.muted, marginBottom: 24 }}>Register and capture face data for a new student.</div>

      {/* Steps */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 28 }}>
        {["Details", "Face Capture", "Review", "Done"].map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", flex: i < 3 ? 1 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: step > i+1 ? C.green : step === i+1 ? C.accent : C.dim, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: step <= i+1 ? "#fff" : C.muted }}>
                {step > i+1 ? "✓" : i+1}
              </div>
              <span style={{ fontSize: 13, color: step === i+1 ? C.text : C.muted, fontWeight: step === i+1 ? 600 : 400 }}>{s}</span>
            </div>
            {i < 3 && <div style={{ flex: 1, height: 1, background: C.dim, margin: "0 12px" }} />}
          </div>
        ))}
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, maxWidth: 540 }}>
        {error && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#DC2626" }}>{error}</div>}

        {/* Step 1 */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Role</label>
              <div style={{ display: "flex", gap: 10 }}>
                {["student", "staff"].map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      if (r === "staff") {
                        setForm(f => ({ ...f, role: r, batch: "Staff", roll: "" }));
                      } else {
                        getNextRollNumber().then(n => setForm(f => ({ ...f, role: r, batch: "Batch A", roll: String(n) })));
                      }
                    }}
                    style={{
                      flex: 1, padding: "10px", borderRadius: 9, border: `1px solid ${form.role === r ? C.accent : C.border}`,
                      background: form.role === r ? C.accentSoft : "none", color: form.role === r ? C.accent : C.muted,
                      fontSize: 14, fontWeight: 600, cursor: "pointer", textTransform: "capitalize", fontFamily: "DM Sans"
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
              {inp("name", "Arjun Sharma", "Full Name")}
              {inp("roll", form.role === "staff" ? "E-101" : "07", form.role === "staff" ? "Staff ID (e.g. E-101)" : "Roll Number")}
              {inp("email", "student@email.com", "Email", "email")}
              {inp("phone", "+91 98765 43210", "Phone")}
              {inp("familyPhone", "+91 98765 43210", "Family Phone")}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, color: C.muted, display: "block", marginBottom: 6, fontWeight: 500 }}>Batch</label>
              {form.role === "staff" ? (
                <input disabled value="Staff" style={{ width: "100%", background: "#F3F4F6", border: `1px solid ${C.border}`, borderRadius: 9, padding: "10px 13px", fontSize: 14, color: C.muted, outline: "none", cursor: "not-allowed", fontFamily: "DM Sans" }} />
              ) : (
                <select value={form.batch} onChange={e => setForm(f => ({ ...f, batch: e.target.value }))} style={{ width: "100%", background: "#F9FAFB", border: `1px solid ${C.border}`, borderRadius: 9, padding: "10px 13px", fontSize: 14, color: C.text, fontFamily: "DM Sans", outline: "none" }}>
                  {BATCHES.map(b => <option key={b}>{b}</option>)}
                </select>
              )}
            </div>
            <button onClick={() => { if (!form.name || !form.roll) { setError("Name and ID are required."); return; } setError(""); setStep(2); }} style={{ width: "100%", padding: 13, borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
              Continue →
            </button>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>Capture Face Samples</div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>Position face in frame. Slightly move left and right for better coverage.</div>

            <div style={{ position: "relative", width: 240, height: 240, margin: "0 auto 16px", borderRadius: 14, overflow: "hidden", background: "#000", border: `2px solid ${capturing ? C.accent : C.border}` }}>
              <video ref={videoRef} autoPlay muted playsInline style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
              {capturing && <div style={{ position: "absolute", left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${C.accent},transparent)`, animation: "scanY 2s ease-in-out infinite" }} />}
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,.6)", padding: "6px", fontSize: 12, color: "#fff", textAlign: "center" }}>
                {capturing ? `${samples} / 5 captured` : "Camera preview"}
              </div>
            </div>

            <style>{`@keyframes scanY{0%,100%{top:0}50%{top:220px}}`}</style>

            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 20 }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{ width: 14, height: 14, borderRadius: "50%", background: i < samples ? C.green : C.dim, transition: "background .3s" }} />
              ))}
            </div>

            {!modelsLoaded && (
              <div style={{ fontSize: 13, color: modelError ? "#DC2626" : C.muted, marginBottom: 12 }}>
                {modelError ? `❌ ${modelError}` : "⏳ Loading face models…"}
              </div>
            )}

            <button onClick={startCapture} disabled={capturing || !modelsLoaded} style={{ width: "100%", padding: 13, borderRadius: 10, background: capturing || !modelsLoaded ? C.dim : C.accent, color: "#fff", border: "none", fontSize: 15, fontWeight: 600, cursor: capturing || !modelsLoaded ? "not-allowed" : "pointer" }}>
              {capturing ? `Capturing ${samples}/5…` : "Start Capture"}
            </button>
          </div>
        )}

        {/* Step 3 — Review */}
        {step === 3 && (
          <div>
            <div style={{ background: "#F9FAFB", borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>Review Details</div>
              {[["Name", form.name], ["Roll", form.roll], ["Batch", form.batch], ["Email", form.email || "—"], ["Family Phone", form.familyPhone || "—"]].map(([l, v]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: C.muted }}>{l}</span>
                  <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{v}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 13, color: C.muted }}>Face samples</span>
                <span style={{ fontSize: 13, color: C.green, fontWeight: 600 }}>✓ {capturedDesc.length} captured</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={saveAndFinish} disabled={saving} style={{ flex: 1, padding: 13, borderRadius: 10, background: C.green, color: "#fff", border: "none", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                {saving ? "Saving…" : "Save Student ✓"}
              </button>
              <button onClick={() => setStep(2)} style={{ padding: "13px 18px", borderRadius: 10, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 14, cursor: "pointer" }}>Re-capture</button>
            </div>
          </div>
        )}

        {/* Step 4 — Done */}
        {step === 4 && (
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.greenSoft, border: `2px solid ${C.green}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, color: C.green, margin: "0 auto 16px" }}>✓</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 6 }}>{isUpdate ? "Face Updated!" : `${form.name} enrolled!`}</div>
            <div style={{ fontSize: 14, color: C.muted, marginBottom: 8 }}>{isUpdate ? "The new face data has been saved successfully." : "Face data saved to Firebase. Student can now use the kiosk."}</div>
            <div style={{ background: "#F9FAFB", borderRadius: 8, padding: "8px 14px", display: "inline-block", marginBottom: 24 }}>
              <code style={{ fontSize: 12, color: C.accent }}>{form.role === "staff" ? "staff" : "students"}/{enrolledId}/faceDescriptors</code>
            </div>
            <br />
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              <button onClick={reset} style={{ padding: "12px 24px", borderRadius: 10, background: "none", border: `1px solid ${C.border}`, color: C.muted, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>{isUpdate ? "Done" : "Enroll Another"}</button>
              <button 
                onClick={() => navigate(`/complete-profile/${enrolledId}`)} 
                style={{ padding: "12px 24px", borderRadius: 10, background: C.accent, color: "#fff", border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >
                {isUpdate ? "View Full Profile →" : "Complete Detailed Profile →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
